import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiBuilding, FiLock, FiMail, FiEye, FiEyeOff, FiHome, FiArrowRight, FiAlertCircle, FiLayers, FiStar } = FiIcons;

const CompanyLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { authenticateUser } = useData();
  const { validateSubmission, HoneypotField } = useFormSecurity('company_login');

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateSubmission()) return;
    
    setIsLoading(true);
    setError('');

    try {
      const authenticatedUser = await authenticateUser(formData.email, formData.password);
      
      if (authenticatedUser) {
        const success = await login(authenticatedUser, authenticatedUser.role);
        if (success) {
          if (authenticatedUser.role === 'superadmin') {
            navigate('/superadmin');
          } else {
            navigate('/company/dashboard');
          }
        } else {
          setError('Kunne ikke opprette sesjon.');
        }
      } else {
        setError('Ugyldig e-post eller passord.');
      }
    } catch (err) {
      console.error("Login error:", err);
      setError('En feil oppstod.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = () => {
    setFormData({ email: 'admin@employerbrand.no', password: 'Employer2024!' });
  };

  // NY: Handler for Konsern Demo (Knappen fra bildet)
  const handleKonsernDemo = async () => {
    setIsLoading(true);
    // Vi fyller inn dataene og trigger login automatisk
    const demoEmail = 'konsern@demo.no';
    const demoPass = 'admin';
    
    try {
      const authenticatedUser = await authenticateUser(demoEmail, demoPass);
      if (authenticatedUser) {
        await login(authenticatedUser, 'admin');
        navigate('/company/dashboard');
      } else {
        // Fallback hvis brukeren ikke finnes i databasen enda (Context håndterer dette, men for sikkerhets skyld)
        setError('Demo-bruker klargjøres... Prøv igjen om 2 sekunder.');
      }
    } catch (e) {
      setError('Kunne ikke starte demo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4 relative">
      <button onClick={() => navigate('/')} className="absolute top-6 left-6 flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors font-medium bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
        <SafeIcon icon={FiHome} /> <span>Tilbake til forsiden</span>
      </button>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <SafeIcon icon={FiBuilding} className="text-white text-2xl" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bedriftsportal</h1>
          <p className="text-gray-600">Logg inn for å administrere HMS og varsler</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          
          {/* NY: KONSERN DEMO KNAPP (Som på bildet) */}
          <button 
            onClick={handleKonsernDemo}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-xl shadow-lg mb-8 flex items-center justify-between hover:from-orange-600 hover:to-orange-700 transition-all transform hover:-translate-y-1 group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-2 rounded-lg">
                <SafeIcon icon={FiLayers} className="text-2xl text-white" />
              </div>
              <div className="text-left">
                <div className="font-black text-sm uppercase tracking-wider">DEMO: KONSERN</div>
                <div className="text-[10px] font-bold text-orange-100">5 AVDELINGER + DASHBOARD</div>
              </div>
            </div>
            <SafeIcon icon={FiStar} className="text-2xl text-yellow-300 animate-pulse" />
          </button>

          <form onSubmit={handleSubmit} className="space-y-6">
            <HoneypotField />
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <SafeIcon icon={FiAlertCircle} className="text-red-600" />
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">E-post</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" placeholder="navn@bedrift.no" required />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Passord</label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" placeholder="••••••••" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                </button>
              </div>
            </div>

            <button type="submit" disabled={isLoading} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg">
              {isLoading ? 'Logger inn...' : 'Logg inn'}
              {!isLoading && <SafeIcon icon={FiArrowRight} />}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100 flex justify-between">
            <button onClick={handleDemoLogin} className="text-xs text-gray-400 hover:text-blue-500 transition-colors font-medium">
              Standard Bedrift
            </button>
            <button onClick={() => navigate('/')} className="text-xs text-gray-400 hover:text-blue-500 transition-colors font-medium">
              Tilbake til start
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CompanyLogin;